import DOMPurify from 'dompurify';
import classNames from 'classnames';
import { ReactNode, useEffect, useState } from 'react';
import { SuggestionDataItem } from 'react-mentions';

import { Avatar, Divider, Option, Select, Space, Spin, UserOutlinedIcon } from 'components-antd';
import { SmartFormIcons, SmartFormIconVariants } from 'components/SmartForm';
import { FormCommentMention } from '../FormCommentMention';
import {
  matchLineBreaksOrMentionsUserTag,
  matchMentionsUserTag,
  matchMentionsUserTagEmail,
  matchTrailingSpacesIgnoringLineBreaks,
  selectAllOption,
  whiteColor,
} from 'app-constants';
import { FormCommentBoxHeader } from '../FormCommentBoxHeader';
import { dynamicCommentOptions, getMappedCommentUsers } from 'pages/FormProcess/helper';
import {
  CommentUsersType,
  CommentUserType,
  CustomOptions,
  EditedCommentType,
  EditFormCommentType,
  FormComment,
} from 'types';

import styles from './styles.module.scss';
import { MessageAccessType } from 'app-constants/enums/message';
import EditActions from 'components/Drawers/components/SendMessageForm/SendMessageFormView/EditActions';

interface FormCommentBoxProps {
  isPanelComment?: boolean;
  isHeader?: boolean;
  isResolved?: boolean;
  threadId?: number;
  isSending: boolean;
  setSending: (isSending: boolean) => void;
  userInfo: CommentUserType;
  comment: FormComment;
  commentUsers: CommentUsersType;
  notifyOptions?: CustomOptions;
  handleEditFormComment?: (
    payload: EditFormCommentType,
    setEditingMode: (editingMode: boolean) => void,
    setSending: (isSending: boolean) => void,
  ) => void;
  handleDeleteFormComment?: (formCommentId: number) => void;
  handleToggleResolveThread?: (
    threadId: number,
    setResolving: (resolving: boolean) => void,
  ) => void;
  commentBoxClass?: string;
  dynamicComment?: boolean;

  permittedUsers: string[];
  setPermittedUsers: (permittedUsers: string[]) => void;
}

export const FormCommentBox = ({
  isPanelComment = false,
  isHeader,
  isResolved,
  threadId,
  isSending,
  setSending,
  userInfo,
  comment,
  commentUsers,
  notifyOptions,
  handleEditFormComment,
  handleDeleteFormComment,
  handleToggleResolveThread,
  commentBoxClass,
  dynamicComment,
  permittedUsers,
  setPermittedUsers,
}: FormCommentBoxProps) => {
  const [tagableUsers, setTagableUsers] = useState<SuggestionDataItem[]>([]);
  const [editingMode, setEditingMode] = useState(false);
  const [editedComment, setEditedComment] = useState<EditedCommentType>({
    comment: comment.Text,
  });

  const [commentAccessType, setCommentAccessType] = useState(
    comment.AccessType || MessageAccessType.Everybody,
  );

  useEffect(() => {
    setTagableUsers(
      notifyOptions?.map((notifyOption) => ({
        id: notifyOption.value,
        display: notifyOption.label,
      })) || [],
    );
  }, [notifyOptions]);

  const handleEditComplete = () => {
    handleEditFormComment &&
      handleEditFormComment(
        {
          formCommentId: comment.Id,
          comment: editedComment.comment.replace(matchTrailingSpacesIgnoringLineBreaks, ' ').trim(),
          tag: editedComment?.mentions?.length
            ? getMappedCommentUsers(commentUsers, editedComment?.mentions)
            : undefined,
          ...(dynamicComment && { accessType: commentAccessType }),
        },
        setEditingMode,
        setSending,
      );
  };

  const checkSendStatus = () => {
    if (editedComment.comment && editedComment.comment.trim() !== comment.Comment) {
      handleEditComplete();
    } else {
      setEditingMode(false);
      setEditedComment({
        comment: comment.Comment,
      });
    }
  };

  const renderComment = (comment: string) => {
    const sanitizedComment = DOMPurify.sanitize(comment);
    const parts = sanitizedComment.split(matchLineBreaksOrMentionsUserTag);

    return (
      <div className={styles.viewComment} onClick={(e) => e.stopPropagation()}>
        {parts.map((part, index) => {
          if (matchMentionsUserTag.test(part)) {
            const email = part.match(matchMentionsUserTagEmail)?.[1];
            const existingUser = Object.values(commentUsers).find(
              (commentUser) => commentUser.email === email,
            );

            if (existingUser?.userId) {
              return (
                <a href="javascript:void(0);" className={styles.mentionedUser}>
                  @{existingUser.name}
                </a>
              );
            } else {
              return (
                <a href={`mailto:${email}`} className={styles.mentionedUser}>
                  {email}
                </a>
              );
            }
          } else if (part === '\n') {
            return <br />;
          } else {
            return <span key={index}>{part}</span>;
          }
        })}
      </div>
    );
  };

  const getOptions = () => {
    if (dynamicComment) return dynamicCommentOptions;

    return [];
  };

  return (
    <div
      key={`comment-box-${comment.Id}`}
      className={classNames(
        styles.commentBox,
        {
          [styles.panelComment]: isPanelComment,
        },
        commentBoxClass,
      )}
    >
      <FormCommentBoxHeader
        isHeader={isHeader}
        isResolved={isResolved}
        threadId={threadId}
        userInfo={userInfo}
        comment={comment}
        setEditingMode={setEditingMode}
        handleDeleteFormComment={handleDeleteFormComment}
        handleToggleResolveThread={handleToggleResolveThread}
        isPanelComment={isPanelComment}
        dynamicComment={dynamicComment}
      />

      <div
        className={classNames(styles.comment, {
          [styles.panelCommentText]: isPanelComment,
        })}
      >
        {editingMode ? (
          <div
            className={classNames(styles.editingMode, {
              [styles.dynamicEditingMode]: dynamicComment,
              [styles.dynamicPanelEditingMode]: dynamicComment && isPanelComment,
            })}
            onClick={(e) => editingMode && e.stopPropagation()}
          >
            <div className={styles.inputContainer}>
              <FormCommentMention
                tagableUsers={tagableUsers}
                editedComment={editedComment}
                setEditedComment={setEditedComment}
              />
            </div>

            {dynamicComment && (
              <div className={styles.setActions}>
                <Select
                  disabled={isSending}
                  suffixIcon={
                    <SmartFormIcons
                      variant={SmartFormIconVariants.Tag}
                      style={{ cursor: 'pointer' }}
                    />
                  }
                  showSearch={false}
                  className={styles.tagSelector}
                  value={[]}
                  showArrow={true}
                  mode={dynamicComment ? undefined : 'tags'}
                  onDropdownVisibleChange={() =>
                    !permittedUsers?.length && setPermittedUsers([selectAllOption.value])
                  }
                  onChange={(users) => {
                    if (dynamicComment) {
                      let { mentions = [] } = editedComment;
                      mentions = Array.from(new Set([...mentions, users]));

                      const taggedUserName = tagableUsers.find(({ id }) => id === users)?.display;
                      const taggedComment = `@[${taggedUserName}](${users})`;
                      setEditedComment((prev) => ({
                        comment: prev.comment + taggedComment,
                        mentions,
                      }));
                    } else {
                      return undefined;
                    }
                  }}
                  popupClassName={styles.notifyOptions}
                  menuItemSelectedIcon={<SmartFormIcons variant={SmartFormIconVariants.Tick} />}
                >
                  {notifyOptions?.map((option, i) => (
                    <Option key={`${option.value}-option-${i}`} value={option.value}>
                      <Avatar src={option.avatar} size={24} icon={<UserOutlinedIcon />} />
                      <span className={styles.tagName}>{option.label}</span>
                      {option.role ? <span className={styles.rolePill}>{option.role}</span> : <></>}
                    </Option>
                  ))}
                </Select>

                <Divider type="vertical" className={styles.actionDivider} />

                <Select
                  disabled={!dynamicComment || isSending}
                  showSearch={false}
                  className={classNames(styles.permissionSelector, {
                    [styles.disabledSelector]: !dynamicComment || isSending,
                    [styles.dynamicAccessLevelSelector]: dynamicComment,
                  })}
                  showArrow={true}
                  mode={undefined}
                  value={commentAccessType}
                  onChange={(selectedValue) => {
                    if (dynamicComment) {
                      setCommentAccessType?.(selectedValue);
                    }
                  }}
                  tagRender={(props) => (
                    <span
                      style={{ background: 'transparent', border: 'none' }}
                      className="ant-select-selection-item"
                    >
                      {props.label}
                    </span>
                  )}
                  popupClassName={styles.permissionOptions}
                  menuItemSelectedIcon={<SmartFormIcons variant={SmartFormIconVariants.Tick} />}
                  dropdownRender={(originNode: ReactNode) => (
                    <div>
                      <div style={{ padding: 10, color: '#AAABAB' }}>
                        Select who can see this comment.
                      </div>
                      {originNode}
                    </div>
                  )}
                >
                  {getOptions().map((option, i) => (
                    <Option
                      key={`${option.value}-option-${i}`}
                      disabled={false}
                      value={option.value}
                    >
                      <Space>
                        {option.icon} {option.label}
                      </Space>
                    </Option>
                  ))}
                </Select>
              </div>
            )}

            <div className={styles.sendContainer}>
              {isSending ? (
                <Spin size="small" className={styles.sendingLoader} />
              ) : dynamicComment ? (
                <EditActions
                  onSave={checkSendStatus}
                  onCancelEdit={() => {
                    setCommentAccessType(MessageAccessType.Everybody);
                    setEditingMode(false);
                  }}
                  show={!!editedComment.comment}
                  disabledSave={
                    !(editedComment.comment && editedComment.comment.trim() !== comment.Comment)
                  }
                />
              ) : (
                <span
                  className={
                    editedComment.comment && editedComment.comment.trim() !== comment.Comment
                      ? styles.sendIconActive
                      : styles.sendIcon
                  }
                  onMouseDown={checkSendStatus}
                >
                  <SmartFormIcons
                    variant={
                      editedComment.comment && editedComment.comment.trim() !== comment.Comment
                        ? SmartFormIconVariants.Send
                        : SmartFormIconVariants.Cross
                    }
                    {...(editedComment.comment &&
                      editedComment.comment.trim() !== comment.Comment && {
                        stroke: whiteColor,
                      })}
                  />
                </span>
              )}
            </div>
          </div>
        ) : (
          renderComment(comment.Text)
        )}
      </div>
    </div>
  );
};
